/* Puppy Kind support routing: Contact Us is the only customer-support path. */
(() => {
  'use strict';

  const contactUrl = '/pages/contact';
  const contactLabels = /^(contact us|email us|live chat|start a live chat|help & contact status|help & contact)$/i;
  const chatCopy = /\bstart a live chat\b|\blive chat\b/gi;

  function removeChatArtifacts(root = document) {
    const selector = 'shopify-chat, .pk-chat-bubble, [data-pk-action="chat"], [data-pm-open-chat], [data-pm-chat-status]';
    if (root.matches?.(selector)) root.remove();
    root.querySelectorAll?.(selector).forEach((node) => node.remove());
  }

  function routeContactLinks(root = document) {
    root.querySelectorAll?.('a[href]').forEach((link) => {
      const label = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (contactLabels.test(label)) {
        link.setAttribute('href', contactUrl);
        link.removeAttribute('data-pm-open-chat');
      }
    });
  }

  function simplifyPortalContact(root = document) {
    const sections = [];
    if (root.matches?.('[data-section="contact"]')) sections.push(root);
    root.querySelectorAll?.('[data-section="contact"]').forEach((section) => sections.push(section));
    sections.forEach((section) => {
      if (section.dataset.pkContactNormalized === 'true') return;
      section.dataset.pkContactNormalized = 'true';
      section.innerHTML = '<h2>How can we help?</h2><p>Send us a message through the Contact Us page and our care team will reply to you.</p><a class="btn full" href="/pages/contact">Contact Us</a>';
    });
  }

  function removeChatCopy(root = document) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (chatCopy.test(node.nodeValue || '')) node.nodeValue = node.nodeValue.replace(chatCopy, 'Contact Us');
      chatCopy.lastIndex = 0;
    });
  }

  function normalize(root = document) {
    removeChatArtifacts(root);
    simplifyPortalContact(root);
    removeChatCopy(root);
    routeContactLinks(root);
  }

  // Capture before portal/preview click handlers so stale chat controls cannot open a dialog.
  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('a[href], [data-pk-action="chat"], [data-pm-open-chat]');
    if (!target) return;
    const label = (target.textContent || '').replace(/\s+/g, ' ').trim();
    if (target.matches('[data-pk-action="chat"], [data-pm-open-chat]') || contactLabels.test(label)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(contactUrl);
    }
  }, true);

  /* Order help (2026-10-08, revised 2026-10-09). "Help with my order" carries the
     order on screen into the Contact Us message, so a member does not retype it:
     the reference, each purchased line with its pack/colour and quantity, and the
     free gifts. Lines are read from the order card's item rows (.pkx-line), never
     from shipment or tracking rows. The context travels in this tab's
     sessionStorage only, never in the URL. The email field is filled only from a
     verified source: the Shopify customer (Liquid) or the verified Puppy Kind
     session once it resolves, and never over anything the customer typed. Nothing
     is sent until the customer presses Send; the test account's send stays
     blocked and the form says so before they try. */
  const CONTEXT_KEY = 'pk-contact-order-context';
  const clean = (value, max = 120) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
  const NOT_ITEMS = /^(shipment|tracking number|pup club plan|order total)\b/i;
  const ownText = (node) => [...(node?.childNodes || [])].filter((child) => child.nodeType === 3).map((child) => child.nodeValue).join(' ');
  function lineLabel(row, { gift = false } = {}) {
    const name = clean(ownText(row.querySelector('.pkx-line-name')) || row.querySelector('.pkx-line-name')?.textContent, 80);
    if (!name || NOT_ITEMS.test(name)) return '';
    const parts = clean(row.querySelector('.pkx-line-meta')?.textContent, 120).split(/\s*·\s*/).filter(Boolean);
    const detail = gift ? parts.filter((part) => !/^qty 1$/i.test(part)) : parts;
    return detail.length ? `${name} (${detail.join(', ')})` : name;
  }
  function orderContext(card) {
    const reference = clean(card.querySelector('.pkx-order-ref, h2')?.textContent, 60);
    const groups = [...card.querySelectorAll('.pkx-group')];
    const labelled = (pattern) => groups.filter((group) => pattern.test(clean(group.querySelector('.pkx-label')?.textContent)));
    const rows = (pattern, gift) => labelled(pattern).flatMap((group) => [...group.querySelectorAll('.pkx-line')].map((row) => lineLabel(row, { gift })));
    let items = rows(/^purchased$/i, false).filter(Boolean);
    const gifts = rows(/^free gifts?$/i, true).filter(Boolean);
    if (!items.length) {
      /* Older card layout: one data-row per purchased line, shipments mixed in. */
      items = labelled(/^purchased$/i).flatMap((group) => [...group.querySelectorAll('.data-row')])
        .map((row) => [clean(row.querySelector('span')?.firstChild?.textContent || row.querySelector('span')?.textContent, 80), clean(row.querySelector('strong')?.textContent, 40)])
        .filter(([name]) => name && !NOT_ITEMS.test(name))
        .map(([name, detail]) => (detail ? `${name} (${detail})` : name));
    }
    return { reference, items: items.slice(0, 8), gifts: gifts.slice(0, 8) };
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-pk-order-help]');
    if (!link) return;
    const card = document.querySelector('[data-pk-order-history] .pkx-order, [data-pk-order-history] .order-card');
    if (!card) return;
    const { reference, items, gifts } = orderContext(card);
    if (!reference && !items.length) return;
    try { sessionStorage.setItem(CONTEXT_KEY, JSON.stringify({ reference, items, gifts, at: Date.now() })); } catch (_) { /* storage blocked: plain form */ }
  }, true);

  const DEMO_NOTE = 'This is a test account, so Send is turned off. Nothing will be sent.';
  function demoAccount() {
    try { if (window.PKDemoActionLock?.identity?.() === 'demo') return true; } catch (_) {}
    try { const club = window.PKPupClub; return club?.signedIn?.() === true && club.isReviewAccount?.() === true; } catch (_) { return false; }
  }

  function prefillContact() {
    const body = document.querySelector('[data-pk-contact-body]');
    if (!body) return;
    const form = body.form || body.closest('form');
    const note = document.querySelector('[data-pk-contact-context]');
    const email = document.querySelector('[data-pk-contact-email]');
    let context = null;
    try { context = JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || 'null'); } catch (_) { context = null; }
    if (context && !(Date.now() - Number(context.at) < 30 * 60 * 1000)) context = null;
    if (context) { try { sessionStorage.removeItem(CONTEXT_KEY); } catch (_) {} }
    let added = '';
    if (context && !body.value.trim()) {
      const list = (value) => (Array.isArray(value) ? value.map((item) => clean(item)).filter(Boolean) : []);
      const items = list(context.items), gifts = list(context.gifts);
      const lines = [];
      if (context.reference) lines.push(`Order: ${clean(context.reference, 60)}`);
      if (items.length) lines.push(`Items: ${items.join(', ')}`);
      if (gifts.length) lines.push(`Free gifts: ${gifts.join(', ')}`);
      lines.push('', 'How can we help? ');
      body.value = lines.join('\n');
      added = `We added ${context.reference ? `order ${clean(context.reference, 60)}` : 'your order'} to your message.`;
      body.focus({ preventScroll: true });
      body.setSelectionRange(body.value.length, body.value.length);
    }

    /* Status line: what was added, and for the test account that Send is off. The
       demo action lock still blocks the post itself; this only says so up front. */
    const paintNote = () => {
      if (!note) return;
      const demo = demoAccount();
      const text = demo ? [added, DEMO_NOTE].filter(Boolean).join(' ') : added ? `${added} Add your question and press Send.` : '';
      if (!text) return;
      if (note.textContent !== text) note.textContent = text;
      note.hidden = false;
      note.classList.toggle('is-demo', demo);
    };

    /* Verified email: filled once the account resolves, only into an empty field
       the customer has not touched. The auth client does not announce its first
       verification on this page, so a short bounded re-check covers that gap. */
    let typed = false, done = !email, timer = 0, tries = 0;
    if (email) email.addEventListener('input', () => { typed = true; }, { once: true });
    const fillEmail = () => {
      if (done) return;
      if (typed || email.value.trim()) { done = true; return; }
      try {
        const club = window.PKPupClub;
        const verified = club?.signedIn?.() === true && typeof club.verifiedEmail === 'function' ? clean(club.verifiedEmail(), 254) : '';
        if (verified && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verified)) { email.value = verified; done = true; }
      } catch (_) {}
    };
    const refresh = () => { fillEmail(); paintNote(); if (done && timer) { clearInterval(timer); timer = 0; } };
    ['pk:auth-state', 'pk:member-state', 'pk:identity'].forEach((name) => addEventListener(name, refresh));
    refresh();
    if (!done) timer = setInterval(() => { if (++tries > 60) { clearInterval(timer); timer = 0; } refresh(); }, 500);

    /* Second guard behind the demo action lock: the test account never posts. */
    if (form) form.addEventListener('submit', (event) => {
      if (!demoAccount()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (note) { note.textContent = DEMO_NOTE; note.hidden = false; note.classList.add('is-demo'); }
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prefillContact);
  else prefillContact();

  normalize();
  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) normalize(node);
    }));
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
})();
